import { useReducer, useEffect, useMemo, useCallback } from 'react';
import { GameOutput, LeagueOutput } from '../generated-api';
import { RankingEntity, Team, TEAM_COLORS, TEAM_ANIMAL_NAMES } from '../components/SortableEntity';

const STORAGE_KEY = 'playWizardState';
const OLD_STORAGE_KEY = 'livePlayWizardState';

export interface WizardState {
  currentStep: number;

  // Step 1
  playType: 'live' | 'record' | null;

  // Step 2
  selectedGameId: string;
  gameExtensions: string[];

  // Step 3
  selectedPlayerIds: string[];
  teams: Team[];

  // Step 4 (record mode)
  rankingEntities: RankingEntity[];
  playDate: string;
  comment: string;
  durationMinutes: number | null;
  teamMetadata: Record<string, Record<string, any>>;
  playImages: File[];

  // Step 4 (shared)
  selectedLeagueIds: string[];

  // Live play tracking
  fromLivePlayId: string | null;

  // Meta
  isSubmitting: boolean;
  error: string | null;
}

export type WizardAction =
  | { type: 'SELECT_GAME'; gameId: string }
  | { type: 'DESELECT_GAME' }
  | { type: 'TOGGLE_EXTENSION'; extension: string }
  | { type: 'TOGGLE_PLAYER'; playerId: string }
  | { type: 'CYCLE_TEAM'; playerId: string }
  | { type: 'TOGGLE_LEAGUE'; leagueId: string; leagues: LeagueOutput[] }
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'GO_TO_STEP'; step: number }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_SUBMITTING'; value: boolean }
  | { type: 'SET_LEAGUE_IDS'; leagueIds: string[] }
  | { type: 'SET_PLAY_TYPE'; playType: 'live' | 'record' }
  | { type: 'SET_RANKING_ENTITIES'; entities: RankingEntity[] }
  | { type: 'UPDATE_RANKING_ENTITIES'; updater: (prev: RankingEntity[]) => RankingEntity[] }
  | { type: 'SET_PLAY_DATE'; date: string }
  | { type: 'SET_COMMENT'; comment: string }
  | { type: 'SET_DURATION'; minutes: number | null }
  | { type: 'SET_TEAM_METADATA'; metadata: Record<string, Record<string, any>> }
  | { type: 'SET_PLAY_IMAGES'; images: File[] }
  | {
      type: 'INIT_FROM_LIVE_PLAY';
      gameId: string;
      gameExtensions: string[];
      leagueIds: string[];
      playerIds: string[];
      teams: Team[];
      playDate: string;
      durationMinutes: number | null;
      teamMetadata: Record<string, Record<string, any>>;
      fromLivePlayId: string;
      rankingEntities: RankingEntity[];
    }
  | {
      type: 'INIT_FROM_REMATCH';
      gameId: string;
      gameExtensions: string[];
      leagueIds: string[];
      playerIds: string[];
      teams: Team[];
    }
  | { type: 'RESET' };

const MAX_TEAMS = 5;

function pickAnimalName(existingTeams: Team[]): string {
  const usedNames = new Set(existingTeams.map(t => t.name));
  const available = TEAM_ANIMAL_NAMES.filter(a => !usedNames.has(`Team ${a}`));
  if (available.length === 0) return `Team ${Math.floor(Math.random() * 1000)}`;
  return `Team ${available[Math.floor(Math.random() * available.length)]}`;
}

function getMaxStep(playType: 'live' | 'record' | null): number {
  return playType === 'live' ? 5 : 6;
}

function cycleTeam(state: WizardState, playerId: string): WizardState {
  const teamColorsToUse = TEAM_COLORS.slice(0, MAX_TEAMS);
  const currentTeam = state.teams.find(t => t.playerIds.includes(playerId));

  if (!currentTeam) {
    // Assign to first team color
    const firstColor = teamColorsToUse[0];
    const existingTeam = state.teams.find(t => t.color === firstColor.name.toLowerCase());

    if (existingTeam) {
      return {
        ...state,
        teams: state.teams.map(t =>
          t.id === existingTeam.id
            ? { ...t, playerIds: [...t.playerIds, playerId] }
            : t
        ),
      };
    } else {
      const newTeam: Team = {
        id: `team-${Date.now()}`,
        name: pickAnimalName(state.teams),
        color: firstColor.name.toLowerCase(),
        playerIds: [playerId],
      };
      return { ...state, teams: [...state.teams, newTeam] };
    }
  }

  // Find current color index
  const currentColorIndex = teamColorsToUse.findIndex(
    c => c.name.toLowerCase() === currentTeam.color
  );

  // Remove player from current team
  const teamsWithoutPlayer = state.teams
    .map(t =>
      t.id === currentTeam.id
        ? { ...t, playerIds: t.playerIds.filter(id => id !== playerId) }
        : t
    )
    .filter(t => t.playerIds.length > 0);

  if (currentColorIndex === -1 || currentColorIndex === MAX_TEAMS - 1) {
    // Remove from team (cycle back to none)
    return { ...state, teams: teamsWithoutPlayer };
  }

  // Move to next team color
  const nextColor = teamColorsToUse[currentColorIndex + 1];
  const nextTeam = teamsWithoutPlayer.find(t => t.color === nextColor.name.toLowerCase());

  if (nextTeam) {
    return {
      ...state,
      teams: teamsWithoutPlayer.map(t =>
        t.id === nextTeam.id
          ? { ...t, playerIds: [...t.playerIds, playerId] }
          : t
      ),
    };
  } else {
    const newTeam: Team = {
      id: `team-${Date.now()}-${nextColor.name}`,
      name: pickAnimalName(teamsWithoutPlayer),
      color: nextColor.name.toLowerCase(),
      playerIds: [playerId],
    };
    return { ...state, teams: [...teamsWithoutPlayer, newTeam] };
  }
}

function toggleLeague(
  state: WizardState,
  leagueId: string,
  leagues: LeagueOutput[]
): WizardState {
  const league = leagues.find(l => l.id === leagueId);
  const globalLeague = leagues.find(l => l.isGlobal);
  const isSelected = state.selectedLeagueIds.includes(leagueId);
  const isUnranked = league?.name === 'Unranked';

  if (isUnranked) {
    // Selecting Unranked deselects everything else; deselecting goes back to Global
    if (isSelected) {
      return {
        ...state,
        selectedLeagueIds: globalLeague ? [globalLeague.id] : [],
      };
    }
    return { ...state, selectedLeagueIds: [leagueId] };
  }

  if (league?.isGlobal) {
    // Global cannot be deselected if it's the only one
    if (isSelected && state.selectedLeagueIds.length === 1) {
      return state;
    }
  }

  let newIds: string[];
  if (isSelected) {
    newIds = state.selectedLeagueIds.filter(id => id !== leagueId);
  } else {
    // Deselect Unranked when selecting a normal league
    const withoutUnranked = state.selectedLeagueIds.filter(id => {
      const l = leagues.find(le => le.id === id);
      return l?.name !== 'Unranked';
    });
    newIds = [...withoutUnranked, leagueId];
  }

  // Ensure Global is always included (unless Unranked is selected)
  const hasUnranked = newIds.some(id => {
    const l = leagues.find(le => le.id === id);
    return l?.name === 'Unranked';
  });
  if (!hasUnranked && globalLeague && !newIds.includes(globalLeague.id)) {
    newIds.push(globalLeague.id);
  }

  return { ...state, selectedLeagueIds: newIds };
}

function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  switch (action.type) {
    case 'SET_PLAY_TYPE':
      return { ...state, playType: action.playType, error: null };
    case 'SELECT_GAME':
      return {
        ...state,
        selectedGameId: action.gameId,
        gameExtensions: [],
        error: null,
      };
    case 'DESELECT_GAME':
      return {
        ...state,
        selectedGameId: '',
        gameExtensions: [],
        error: null,
      };
    case 'TOGGLE_EXTENSION': {
      const exts = state.gameExtensions.includes(action.extension)
        ? state.gameExtensions.filter(e => e !== action.extension)
        : [...state.gameExtensions, action.extension];
      return { ...state, gameExtensions: exts };
    }
    case 'TOGGLE_PLAYER': {
      const isSelected = state.selectedPlayerIds.includes(action.playerId);
      const newPlayerIds = isSelected
        ? state.selectedPlayerIds.filter(id => id !== action.playerId)
        : [...state.selectedPlayerIds, action.playerId];

      // If deselecting, also remove from teams
      let newTeams = state.teams;
      if (isSelected) {
        newTeams = state.teams
          .map(t => ({
            ...t,
            playerIds: t.playerIds.filter(id => id !== action.playerId),
          }))
          .filter(t => t.playerIds.length > 0);
      }

      return { ...state, selectedPlayerIds: newPlayerIds, teams: newTeams, error: null };
    }
    case 'CYCLE_TEAM':
      return cycleTeam(state, action.playerId);
    case 'TOGGLE_LEAGUE':
      return toggleLeague(state, action.leagueId, action.leagues);
    case 'NEXT_STEP': {
      const maxStep = getMaxStep(state.playType);
      return {
        ...state,
        currentStep: Math.min(state.currentStep + 1, maxStep),
        error: null,
      };
    }
    case 'PREV_STEP':
      return {
        ...state,
        currentStep: Math.max(state.currentStep - 1, 1),
        error: null,
      };
    case 'GO_TO_STEP':
      return { ...state, currentStep: action.step, error: null };
    case 'SET_ERROR':
      return { ...state, error: action.error };
    case 'SET_SUBMITTING':
      return { ...state, isSubmitting: action.value };
    case 'SET_LEAGUE_IDS':
      return { ...state, selectedLeagueIds: action.leagueIds };
    case 'SET_RANKING_ENTITIES':
      return { ...state, rankingEntities: action.entities };
    case 'UPDATE_RANKING_ENTITIES':
      return { ...state, rankingEntities: action.updater(state.rankingEntities) };
    case 'SET_PLAY_DATE':
      return { ...state, playDate: action.date };
    case 'SET_COMMENT':
      return { ...state, comment: action.comment };
    case 'SET_DURATION':
      return { ...state, durationMinutes: action.minutes };
    case 'SET_TEAM_METADATA':
      return { ...state, teamMetadata: action.metadata };
    case 'SET_PLAY_IMAGES':
      return { ...state, playImages: action.images };
    case 'INIT_FROM_LIVE_PLAY':
      return {
        ...defaultState,
        playType: 'record',
        selectedGameId: action.gameId,
        gameExtensions: action.gameExtensions,
        selectedLeagueIds: action.leagueIds,
        selectedPlayerIds: action.playerIds,
        teams: action.teams,
        playDate: action.playDate,
        durationMinutes: action.durationMinutes,
        teamMetadata: action.teamMetadata,
        fromLivePlayId: action.fromLivePlayId,
        rankingEntities: action.rankingEntities,
        currentStep: 6, // Go directly to ranking step
      };
    case 'INIT_FROM_REMATCH':
      return {
        ...defaultState,
        playType: null,
        selectedGameId: action.gameId,
        gameExtensions: action.gameExtensions,
        selectedLeagueIds: action.leagueIds,
        selectedPlayerIds: action.playerIds,
        teams: action.teams,
        currentStep: 1, // Let user choose Live or Track Results
      };
    case 'RESET':
      return { ...defaultState };
    default:
      return state;
  }
}

const defaultState: WizardState = {
  currentStep: 1,
  playType: null,
  selectedGameId: '',
  gameExtensions: [],
  selectedPlayerIds: [],
  teams: [],
  rankingEntities: [],
  playDate: '',
  comment: '',
  durationMinutes: null,
  teamMetadata: {},
  playImages: [],
  selectedLeagueIds: [],
  fromLivePlayId: null,
  isSubmitting: false,
  error: null,
};

interface UseWizardStateOptions {
  preselectedGameId?: string;
  preselectedLeagueId?: string;
  preselectedMode?: 'live' | 'record';
}

export function useWizardState(options: UseWizardStateOptions = {}) {
  const initialState = useMemo(() => {
    // Clear old storage keys
    sessionStorage.removeItem(OLD_STORAGE_KEY);
    sessionStorage.removeItem('newPlayStep1State');

    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          ...defaultState,
          ...parsed,
          // Always reset transient / non-persisted state
          isSubmitting: false,
          error: null,
          playImages: [],
          rankingEntities: [],
        };
      } catch {
        // ignore parse errors
      }
    }

    const state = { ...defaultState };
    if (options.preselectedMode) {
      state.playType = options.preselectedMode;
    }
    if (options.preselectedGameId) {
      state.selectedGameId = options.preselectedGameId;
      state.currentStep = state.playType ? 2 : 1;
    }
    if (options.preselectedLeagueId) {
      state.selectedLeagueIds = [options.preselectedLeagueId];
    }
    return state;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [state, dispatch] = useReducer(wizardReducer, initialState);

  // Persist to sessionStorage on every change (excluding non-serializable/transient fields)
  useEffect(() => {
    const { isSubmitting, error, playImages, rankingEntities, ...persistable } = state;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(persistable));
  }, [state]);

  const clearStorage = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(OLD_STORAGE_KEY);
  }, []);

  const isStepValid = useCallback(
    (step: number, game: GameOutput | null): boolean => {
      switch (step) {
        case 1:
          return state.playType !== null;
        case 2:
          return state.selectedGameId !== '';
        case 3:
          return state.selectedPlayerIds.length >= 2;
        case 4: {
          // Team count validation
          if (game) {
            const teamsWithPlayers = state.teams.filter(t => t.playerIds.length > 0);
            const playersNotInTeams = state.selectedPlayerIds.filter(
              pid => !state.teams.some(t => t.playerIds.includes(pid))
            );
            const totalTeamCount = teamsWithPlayers.length + playersNotInTeams.length;

            if (game.minTeams != null && totalTeamCount < game.minTeams) return false;
            if (game.maxTeams != null && totalTeamCount > game.maxTeams) return false;
          }
          return true;
        }
        case 5:
          // Review step: leagues + game + players all valid
          if (state.selectedLeagueIds.length < 1) return false;
          if (state.selectedGameId === '') return false;
          if (state.selectedPlayerIds.length < 2) return false;
          return true;
        case 6:
          // Ranking + Details step: ranking entities present
          return state.rankingEntities.length > 0;
        default:
          return false;
      }
    },
    [state.playType, state.selectedGameId, state.selectedPlayerIds, state.selectedLeagueIds, state.teams, state.rankingEntities]
  );

  return { state, dispatch, isStepValid, clearStorage };
}
